import { Link } from 'react-router-dom'
import { AlertTriangle, Megaphone, Pin } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import type { FeedItem } from '@/hooks/useNewsFeed'

type Props = { item: Extract<FeedItem, { type: 'announcement' }> }

const PRIORITY_STYLES = {
  urgent: {
    border: 'border-red-300 dark:border-red-800',
    bg: 'bg-red-50/50 dark:bg-red-950/20',
    badge: 'bg-red-600 text-white',
    icon: AlertTriangle,
    label: 'URGENT',
  },
  important: {
    border: 'border-amber-300 dark:border-amber-800',
    bg: 'bg-amber-50/50 dark:bg-amber-950/20',
    badge: 'bg-amber-500 text-white',
    icon: Pin,
    label: 'IMPORTANT',
  },
  normal: {
    border: 'border-border',
    bg: 'bg-card',
    badge: 'bg-primary text-primary-foreground',
    icon: Megaphone,
    label: 'ANNOUNCEMENT',
  },
}

export function AnnouncementCard({ item }: Props) {
  const style = PRIORITY_STYLES[item.priority] ?? PRIORITY_STYLES.normal
  const Icon = style.icon

  return (
    <Link to={`/announcement/${item.id}`} className="block">
      <article
        className={`rounded-xl border ${style.border} ${style.bg} overflow-hidden hover:shadow-md transition-shadow`}
      >
        {item.image_url && (
          <div className="aspect-video bg-muted">
            <img src={item.image_url} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-4 space-y-2">
          <div className="flex items-center gap-2 text-xs">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold ${style.badge}`}
            >
              <Icon className="h-3 w-3" />
              {style.label}
            </span>
            {item.category && (
              <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                {item.category}
              </span>
            )}
            <span className="text-muted-foreground ml-auto">
              {formatDistanceToNow(new Date(item.published_at), { addSuffix: true })}
            </span>
          </div>

          <h2 className="text-base font-semibold leading-tight line-clamp-2">{item.title}</h2>

          <p className="text-sm text-muted-foreground line-clamp-3">
            {(item.body ?? '').replace(/[#*_`>~[\]()]/g, '').slice(0, 200)}
          </p>

          <p className="text-xs text-muted-foreground pt-1">
            From: {item.author_entity_name}
          </p>
        </div>
      </article>
    </Link>
  )
}
